var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import z from "zod";
import { useHistory } from "react-router-dom";
import classnames from "classnames";
import ContentLayout from "../../Layout/ContentLayout";
import CustomCard from "../../UI/CustomCard/CustomCard";
import useParams from "../../Hooks/useParams";
import { getMeetingsBaseRoute } from "./MeetingsRoutes";
import { AlertError, AlertInfo, AlertSuccess, } from "../../UI/StandardAlerts/StandardAlerts";
import useService from "../../Hooks/useService";
import ServiceOfferingService from "../../../Services/ServiceOffering.service";
import DateService from "../../../Services/Shared/Date.service";
import MeetingModalityService from "../../../Services/MeetingModality.service";
import PersonService from "../../../Services/Person.service";
import ServiceResult, { Loaders } from "../../UI/ServiceResult/ServiceResult";
import InfoItems from "../../UI/InfoItems/InfoItems";
import { BasicFormFormat } from "../../UI/FormFormat/FormFormat";
import TextField from "../../Form/TextField";
import MeetingModalityDomainService from "../../../Domain/MeetingModality/MeetingModality.service";
import MeetingService from "../../../Services/Meeting.service";
import useCurrentUser from "../../Hooks/useCurrentUser";
import ServiceDeliveryLocationService from "../../../Services/ServiceDeliveryLocation.service";
import MeetingAvailabilityService from "../../../Services/MeetingAvailability.service";
import ProfilePicture from "../../Presentational/Person/ProfilePicture/ProfilePicture";
import { SaveButton } from "../../UI/StandardButtons/StandardButtons";
import useConfirmation from "../../Hooks/useConfirmation";
import { advisorHomeLink, studentHomePath } from "../Advisor/AdvisorRoutes";
import useVirtualizedApp from "../../Hooks/useVirtualizedApp";
import RoleService from "../../../Services/Role.service";
import StringUtils from "../../../../util/stringUtils";
import TelephoneField from "../../Form/TelephoneField";
export const getMeetingConfirmationRoute = () => getMeetingsBaseRoute() + "/confirm";
const PROFILE_PICTURE_SIZE_PX = 40;
const MEETINGS_CONFIRMATION_SEARCH_PARAMS = z.object({
    serviceId: z.coerce.number(),
    staffId: z.string().uuid(),
    startTime: z.coerce.date(),
    endTime: z.coerce.date(),
    officeId: z.coerce.number(),
    modalityId: z.coerce.number(),
    // affiliationId, this is provided through the Meetings Tab UI
    studentId: z.string().uuid().optional(),
});
const MeetingConfirmationHeader = ({ meetingStudent, meetingStaff, currentUser, showStaffNames, }) => {
    if (showStaffNames) {
        return (_jsxs("div", { className: "d-flex flex-row align-items-center", children: [_jsxs("div", { className: "position-relative mr-2", style: {
                        width: `${PROFILE_PICTURE_SIZE_PX * 1.5}px`,
                        height: `${PROFILE_PICTURE_SIZE_PX}px`,
                    }, children: [_jsx("div", { style: {
                                position: "absolute",
                                top: "0px",
                                left: `${PROFILE_PICTURE_SIZE_PX / 2}px`,
                                zIndex: 1,
                            }, children: meetingStudent && (_jsx(ProfilePicture, { person: meetingStudent, size: PROFILE_PICTURE_SIZE_PX, showProfilePicture: true, className: "shadow" })) }), _jsx("div", { style: {
                                position: "absolute",
                                top: "0px",
                                left: "0px",
                                zIndex: 2,
                            }, children: _jsx(ProfilePicture, { person: meetingStaff, size: PROFILE_PICTURE_SIZE_PX, showProfilePicture: true, className: "shadow" }) })] }), _jsx("div", { children: (currentUser === null || currentUser === void 0 ? void 0 : currentUser.personId) === (meetingStudent === null || meetingStudent === void 0 ? void 0 : meetingStudent.personId)
                        ? `Review Meeting with ${meetingStaff.fullName}`
                        : `Review Meeting between ${meetingStaff.fullName} and ${meetingStudent === null || meetingStudent === void 0 ? void 0 : meetingStudent.fullName}` })] }));
    }
    return (_jsxs("div", { className: "d-flex flex-row align-items-center", children: [_jsx("div", { className: "position-relative mr-2", style: {
                    width: `${PROFILE_PICTURE_SIZE_PX}px`,
                    height: `${PROFILE_PICTURE_SIZE_PX}px`,
                }, children: _jsx("div", { children: meetingStudent && (_jsx(ProfilePicture, { person: meetingStudent, size: PROFILE_PICTURE_SIZE_PX, showProfilePicture: true, className: "shadow" })) }) }), _jsx("div", { children: "Review Meeting" })] }));
};
const MeetingConfirmationContent = (props) => {
    var _a;
    const [meetingConfirmationState, loadMeetingConfirmationState] = useService(({ serviceId }) => __awaiter(void 0, void 0, void 0, function* () {
        const [service, offices, modality, staffs, students] = yield Promise.all([
            ServiceOfferingService.get(serviceId),
            ServiceDeliveryLocationService.search({
                ids: [props.searchParams.officeId],
            }),
            MeetingModalityService.get(props.searchParams.modalityId),
            PersonService.searchWithMeetingAvailability({
                ids: [props.searchParams.staffId],
            }),
            props.searchParams.studentId
                ? PersonService.peopleFromAffiliationIds([
                    props.searchParams.studentId,
                ])
                : null,
        ]).catch(() => {
            throw new Error("An error occurred fetching meeting confirmation");
        });
        // We are searching for an office and a staff using an id, we should just get the first one from the results
        const office = offices.data[0];
        const staff = staffs[0];
        const student = students ? students[0] : null;
        return {
            service,
            office,
            staff,
            modality,
            student,
        };
    }));
    const [appointmentSettingsState, getMeetingLink] = useService(MeetingAvailabilityService.getStaffAppointmentSettingsForPersonId);
    useEffect(() => {
        if (props.searchParams.modalityId === MeetingModalityDomainService.online.id) {
            getMeetingLink(props.searchParams.staffId);
        }
    }, [
        getMeetingLink,
        props.searchParams.modalityId,
        props.searchParams.staffId,
    ]);
    useEffect(() => {
        loadMeetingConfirmationState({ serviceId: props.searchParams.serviceId });
    }, [loadMeetingConfirmationState, props.searchParams.serviceId]);
    const virtualizedApp = useVirtualizedApp();
    const saveButtonRef = React.useRef(null);
    const [scheduleMeetingState, scheduleMeeting] = useService(MeetingService.scheduleMeeting);
    const currentUser = useCurrentUser();
    const MeetingConfirmationButtons = () => {
        const history = useHistory();
        const [confirmation, confirmModals] = useConfirmation({
            confirmButtonLabel: "Cancel",
            confirmButtonClassName: "btn btn-secondary",
            cancelButtonLabel: "Close",
        });
        const handleCancelMeetingConfirmationClick = () => __awaiter(void 0, void 0, void 0, function* () {
            confirmation(_jsxs(_Fragment, { children: [_jsx(AlertInfo, { message: "This action you will redirect you to your home page" }), "Are you sure you want to cancel this meeting confirmation?"] })).then((confirmed) => __awaiter(void 0, void 0, void 0, function* () {
                if (confirmed) {
                    // We have to use this because React doesn't do a great job redirecting to non-React pages
                    window.location.assign(virtualizedApp.name !== "student"
                        ? advisorHomeLink({ virtualizedAppName: virtualizedApp.name })
                        : studentHomePath());
                }
            }));
        });
        if (!meetingConfirmationState.data) {
            return null;
        }
        if (scheduleMeetingState.wasSuccessful) {
            return (_jsx("button", { className: "btn btn-outline-secondary ml-2", onClick: () => window.location.assign(virtualizedApp.name !== "student"
                    ? advisorHomeLink({ virtualizedAppName: virtualizedApp.name })
                    : studentHomePath()), children: "Back to Home" }));
        }
        return (_jsxs(_Fragment, { children: [confirmModals, _jsx("div", { children: _jsx("button", { className: "btn btn-outline-secondary", onClick: () => history.goBack(), disabled: meetingConfirmationState.isLoading, children: "Back to Scheduler" }) }), _jsxs("div", { children: [_jsx(SaveButton, { isLoading: meetingConfirmationState.isLoading ||
                                scheduleMeetingState.isLoading, onClick: () => { var _a; return (_a = saveButtonRef.current) === null || _a === void 0 ? void 0 : _a.click(); }, overrideLabel: "Confirm" }), _jsx("button", { className: "btn btn-secondary ml-2", onClick: () => handleCancelMeetingConfirmationClick(), disabled: meetingConfirmationState.isLoading ||
                                scheduleMeetingState.isLoading, children: "Cancel" })] })] }));
    };
    const [isStaffState, loadIsStaff] = useService(RoleService.currentUserIsStaff);
    const [currentStudentState, loadCurrentStudent] = useService(PersonService.getFullSanitizedByAffiliationId);
    React.useEffect(() => {
        var _a;
        loadIsStaff();
        loadCurrentStudent((_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.studentId);
    }, [loadIsStaff, loadCurrentStudent, (_a = props.searchParams) === null || _a === void 0 ? void 0 : _a.studentId]);
    return (_jsx(CustomCard, { className: "mt-4", children: _jsx(ServiceResult, { state: isStaffState, loader: Loaders.overlay(), children: (isStaff) => (_jsx(ServiceResult, { state: currentStudentState, loader: Loaders.overlay(), children: (currentStudent) => (_jsx(ServiceResult, { state: meetingConfirmationState, loader: Loaders.overlay(), children: (meetingConfirmation) => {
                        var _a;
                        const meetingDate = DateService.custom(props.searchParams.startTime, {
                            year: "numeric",
                            month: "long",
                            day: "2-digit",
                            weekday: "long",
                        });
                        const meetingTimeSlot = `${DateService.displayJustTime(props.searchParams.startTime)} - ${DateService.displayJustTime(props.searchParams.endTime)}`;
                        let appointmentDetails;
                        switch (props.searchParams.modalityId) {
                            case MeetingModalityDomainService.physical.id:
                                appointmentDetails = {
                                    label: "Location",
                                    value: `${meetingConfirmation.office.location.name} - ${meetingConfirmation.office.name}`,
                                };
                                break;
                            case MeetingModalityDomainService.online.id:
                                appointmentDetails = {
                                    label: "Online Meeting Link",
                                    value: (_jsx(ServiceResult, { state: appointmentSettingsState, loader: Loaders.skeleton({ loaderHeight: "1rem" }), children: ({ onlineMeetingLink }) => {
                                            if (!onlineMeetingLink) {
                                                return "No meeting link has been provided. Please contact this staff member directly.";
                                            }
                                            return onlineMeetingLink;
                                        } })),
                                };
                                break;
                            case MeetingModalityDomainService.phone.id:
                            default:
                                // don't show anything for phone.  default case included just in case.
                                appointmentDetails = null;
                                break;
                        }
                        // Determines if we display staff names by checking if the service allows viewing
                        // staff names or if the current user is a staff member. This is so staff
                        // members can easily schedule meetings for students and allows staff members
                        // to bypass the service staff selection restriction
                        const showStaffNames = meetingConfirmation.service.showStaffNames ||
                            isStaff ||
                            scheduleMeetingState.wasSuccessful;
                        return (_jsxs(_Fragment, { children: [_jsx("div", { className: "card-header bg-white d-flex justify-content-between", children: _jsx("div", { className: "mb-0 card-title h5 w-100", style: {
                                            minHeight: "40px",
                                        }, children: _jsx(MeetingConfirmationHeader, { meetingStudent: currentStudent, meetingStaff: meetingConfirmation.staff, currentUser: currentUser, showStaffNames: showStaffNames }) }) }), _jsx("div", { className: "card-body", children: _jsxs(BasicFormFormat, { handleSubmit: (state) => __awaiter(void 0, void 0, void 0, function* () {
                                            if (!virtualizedApp.name) {
                                                throw new Error("There was an error retrieving request data.");
                                            }
                                            yield scheduleMeeting({
                                                serviceOfferingId: meetingConfirmation.service.id,
                                                staffPersonId: meetingConfirmation.staff.personId,
                                                studentPersonId: currentStudent.personId,
                                                serviceDeliveryLocationId: meetingConfirmation.office.id,
                                                startTime: props.searchParams.startTime,
                                                studentQuestion: state.studentQuestion,
                                                virtualizedAppName: virtualizedApp.name,
                                                meetingModalityId: meetingConfirmation.modality.id,
                                                meetingStatusId: 1,
                                                studentPhoneNumber: state.studentPhoneNumber
                                                    ? StringUtils.normalizePhoneNumber(state.studentPhoneNumber)
                                                    : null,
                                            });
                                            props.onMeetingConfirmed();
                                        }), defaultValue: {
                                            studentPhoneNumber: (_a = currentStudent.preferredPhone) === null || _a === void 0 ? void 0 : _a.numberWithoutExtension,
                                        }, errorMessagePosition: "bottom", saveButtonRef: saveButtonRef, hideButtons: true, children: [_jsx(InfoItems, { items: [
                                                    {
                                                        label: "Date",
                                                        value: meetingDate,
                                                    },
                                                    {
                                                        label: "Time",
                                                        value: meetingTimeSlot,
                                                    },
                                                    {
                                                        label: "Staff",
                                                        value: showStaffNames
                                                            ? meetingConfirmation.staff.fullName
                                                            : null,
                                                    },
                                                    showStaffNames ? appointmentDetails : null,
                                                    {
                                                        label: "Service",
                                                        value: meetingConfirmation.service.name,
                                                    },
                                                    {
                                                        label: "How do you want to meet?",
                                                        value: (_jsx("span", { className: "badge badge-secondary mr-2", children: meetingConfirmation.modality.name })),
                                                    },
                                                ] }), meetingConfirmation.modality.id ===
                                                MeetingModalityDomainService.phone.id && (_jsx(TelephoneField, { name: "studentPhoneNumber", label: "Phone number I want to be reached at for this meeting", required: true, disabled: scheduleMeetingState.isLoading ||
                                                    scheduleMeetingState.wasSuccessful })), _jsx(TextField, { name: "studentQuestion", label: "Would you like to share anything else?", disabled: scheduleMeetingState.isLoading ||
                                                    scheduleMeetingState.wasSuccessful })] }) }), _jsx("div", { className: classnames("card-footer", {
                                        "d-flex justify-content-end": scheduleMeetingState.wasSuccessful,
                                        "d-flex justify-content-between": !scheduleMeetingState.wasSuccessful,
                                    }), children: _jsx(MeetingConfirmationButtons, {}) })] }));
                    } })) })) }) }));
};
const MeetingConfirmation = () => {
    const params = useParams();
    const safeSearchParams = MEETINGS_CONFIRMATION_SEARCH_PARAMS.safeParse(params);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    if (!safeSearchParams.success) {
        return (_jsx(ContentLayout, { notFluid: true, paddingBottom: true, paddingTop: true, className: "fullMinHeightMinusPrimaryNav", children: _jsx(AlertError, { message: "Invalid request" }) }));
    }
    return (_jsxs(ContentLayout, { notFluid: true, paddingBottom: true, paddingTop: true, className: "fullMinHeightMinusPrimaryNav", children: [showSuccessMessage && (_jsx(AlertSuccess, { message: "Your meeting has been confirmed." })), _jsx(MeetingConfirmationContent, { searchParams: safeSearchParams.data, onMeetingConfirmed: () => setShowSuccessMessage(true) })] }));
};
export default MeetingConfirmation;
